import { Component, Input, OnInit } from '@angular/core'
import {
    DealConfigurationQuery,
    DealType,
    PricingCalculationComponentInsurance,
    PricingCalculationComponentType,
} from 'generated/graphql'
import { BehaviorSubject, Observable } from 'rxjs'
import { getDealOptionKey, getDealOptionValue } from 'modules/deals/utilities/deal-helpers'
import { BooleanDealOption, SelectableDealOption } from 'modules/deals/utilities/deal-types'
import { OptionTranslationPipe } from 'modules/deals/ui/pipes/option-translation.pipe'
import { LocaleService } from 'framework/locale/services/locale.service'
import { DealMutationService } from 'modules/deals/services/dealMutation.service'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { FormControl } from '@angular/forms'
import {
    ConfigurationRoute,
    ConfigurationRouteService,
} from 'modules/deals/configurator/factory/services/configurationRoute.service'
import { filter, first, map, mergeMap, tap } from 'rxjs/operators'
import { ActionBannerService } from 'modules/deals/configurator/services/action-banner.service'
import { TenantService } from '../../../../../../framework/tenant/tenant.service'
import { SubsidyBannerService } from 'modules/deals/configurator/services/sepp-subsidy-banner.service'

export interface BooleanCarOption {
    label: string
    isIncluded: boolean
}

interface CarOptions {
    paint?: string
    rims?: string
    steeringWheel?: string
    interior?: string
    autopilot?: string
    towhitch?: BooleanCarOption
    accessories?: Accessories
}

interface Accessories {
    accessory_towhitch?: BooleanCarOption
    accessory_tesla_wall_connector?: BooleanCarOption
    accessory_bicycle_carrier?: BooleanCarOption
    accessory_roof_rack?: BooleanCarOption
    accessory_css_adapter?: BooleanCarOption
    accessory_home_charge_cable?: BooleanCarOption
}
interface ContractOption {
    title: string
    value: string
}

interface ContractOptions {
    mileage?: ContractOption
    duration?: ContractOption
    downPayment?: ContractOption
    deductible?: ContractOption
    winterTires?: ContractOption
    premiumConnectivity?: ContractOption
}

@UntilDestroy()
@Component({
    selector: 'mg-checkout-sidebar',
    templateUrl: './checkout-sidebar.component.html',
    styleUrls: ['./checkout-sidebar.component.scss'],
})
export class CheckoutSidebarComponent implements OnInit {
    prettyModelName: string
    edition: string
    carOptions: CarOptions
    contractOptions: ContractOptions
    locale: string
    country: string
    isLoadingAmount$: Observable<boolean>
    recentPrices: number[] = []
    leasingType$: Observable<SelectableDealOption>
    leasingTypeControl: FormControl
    route$: Observable<ConfigurationRoute>
    dealId: string
    subsidyShowBanner: boolean
    subsidyBannerContent: string
    subsidyBannerColor: string
    actionBannerContent: string
    actionBannerColor: string
    isCarNew: boolean

    @Input() deal$: Observable<DealConfigurationQuery['Deal']>
    @Input() isOpenOnMobile: boolean
    @Input() modelName: string
    tenantHasBijtelling$: BehaviorSubject<boolean>
    tenantHasVAA_ATN$: BehaviorSubject<boolean>

    constructor(
        private dealMutationService: DealMutationService,
        private localeService: LocaleService,
        private optionTranslationPipe: OptionTranslationPipe,
        private configurationRouteService: ConfigurationRouteService,
        private actionBannerService: ActionBannerService,
        private subsidyBannerService: SubsidyBannerService,
        private tenantService: TenantService
    ) {}

    ngOnInit() {
        this.tenantHasBijtelling$ = this.tenantService.hasBijtelling$
        this.tenantHasVAA_ATN$ = this.tenantService.hasVAA_ATN$

        this.isLoadingAmount$ = this.dealMutationService.isUpdating.pipe(untilDestroyed(this))

        this.country = this.localeService.country
        this.locale = this.localeService.locale

        this.deal$.pipe(untilDestroyed(this)).subscribe(deal => {
            this.prettyModelName = 'Tesla ' + deal.modelName
            this.edition = deal.editionName
            this.dealId = deal.id
            this.isCarNew = deal.dealType === DealType.ConfigurableCar

            const newPrice = deal.pricing.incVAT ? deal.pricing.pricePerMonthIncVAT : deal.pricing.pricePerMonthExVAT
            this.recentPrices = [Math.round(newPrice), ...this.recentPrices].slice(0, 3)

            const { carOptions, contractOptions } = deal.options.reduce(
                (options, option) => {
                    const deductiblePrice: PricingCalculationComponentInsurance = JSON.parse(
                        deal.calculationComponents
                    ).find(x => x.component.type === PricingCalculationComponentType.Insurance)

                    const key = getDealOptionKey(option)

                    if (['paint', 'rims', 'autopilot', 'steeringWheel'].includes(key)) {
                        options.carOptions[key] = (option as SelectableDealOption).selectedValue.label
                    }

                    if (['interior'].includes(key)) {
                        const selectedValue = (option as SelectableDealOption).selectedValue

                        this.optionTranslationPipe
                            .transform({
                                optionName: key,
                                key: 'optionLabel',
                                selectableOptionId: selectedValue.labelId,
                            })
                            .then(label => {
                                options.carOptions[key] = label || selectedValue.label
                            })
                    }
                    if (
                        [
                            'accessory_towhitch',
                            'accessory_tesla_wall_connector',
                            'accessory_bicycle_carrier',
                            'accessory_roof_rack',
                            'accessory_ccs_adapter',
                            'accessory_home_charge_cable',
                            'accessory_extra_long_cable',
                        ].includes(key)
                    ) {
                        const isIncluded = this.checkBooleanDealOptionInclusion(option)

                        this.optionTranslationPipe
                            .transform({
                                optionName: key,
                                key: isIncluded ? 'sidebar-included' : 'sidebar-not-included',
                            })
                            .then(label => {
                                options.carOptions[key] = { label, isIncluded }
                            })
                    }

                    if (['mileage', 'duration', 'downpayment'].includes(key)) {
                        this.optionTranslationPipe
                            .transform({
                                optionName: key,
                                key: 'title',
                            })
                            .then(title => {
                                options.contractOptions[key] = {
                                    title,
                                    value: (option as SelectableDealOption).selectedValue.label,
                                }
                            })
                    }

                    if (['winterTires', 'premiumConnectivity'].includes(key)) {
                        const isIncluded = this.checkBooleanDealOptionInclusion(option)

                        Promise.all([
                            this.optionTranslationPipe.transform({
                                optionName: key,
                                key: 'title',
                            }),
                            this.optionTranslationPipe.transform({
                                optionName: key,
                                key: isIncluded ? 'sidebar-included' : 'sidebar-not-included',
                            }),
                        ]).then(([title, value]) => {
                            options.contractOptions[key] = { title, value }
                        })
                    }

                    if ('deductible'.includes(key)) {
                        const isIncluded = this.checkBooleanDealOptionInclusion(option)

                        Promise.all([
                            this.optionTranslationPipe.transform({
                                optionName: key,
                                key: 'title',
                            }),
                            this.optionTranslationPipe.transform({
                                optionName: key,
                                key: isIncluded ? 'sidebar-included' : 'sidebar-not-included',
                            }),
                        ]).then(([title, value]) => {
                            if (option.__typename !== 'SelectableDealOption') {
                            } else if (option.selectedValue.label.toLowerCase().indexOf('standard rate') > -1) {
                                options.contractOptions[key] = {
                                    title,
                                    value: `€ ${deductiblePrice.component.deductible.regular}`,
                                }
                            } else if (option.selectedValue.label.toLowerCase().indexOf('lower rate') > -1) {
                                options.contractOptions[key] = {
                                    title,
                                    value: `€ ${deductiblePrice.component.deductible.lower}`,
                                }
                            } else if (option.selectedValue.label.toLowerCase().indexOf('higher rate') > -1) {
                                options.contractOptions[key] = {
                                    title,
                                    value: `€ ${deductiblePrice.component.deductible.higher}`,
                                }
                            }
                        })
                    }

                    return options
                },
                { carOptions: {}, contractOptions: {} }
            )

            this.carOptions = carOptions
            this.contractOptions = contractOptions
        })

        this.route$ = this.deal$.pipe(
            untilDestroyed(this),
            mergeMap(deal => this.configurationRouteService.getActiveConfigurationRoute(deal.id))
        )

        this.leasingType$ = this.deal$.pipe(
            untilDestroyed(this),
            map(deal => {
                return deal.options.find(
                    opt => opt.__typename === 'SelectableDealOption' && opt.name === 'leasingType'
                ) as SelectableDealOption
            }),
            filter(v => !!v),
            tap(leasingType => {
                if (!this.leasingTypeControl) {
                    this.leasingTypeControl = new FormControl(leasingType.selectedValue.id)

                    this.leasingTypeControl.valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
                        this.dealMutationService
                            .updateDealOption(this.dealId, leasingType, value)
                            .pipe(first())
                            .subscribe()
                    })
                }
            })
        )

        this.deal$.pipe(untilDestroyed(this)).subscribe(deal => {
            let { content, shouldShowContent, color } = this.subsidyBannerService.retrieveSubsidyBannerDetails(deal)

            this.subsidyBannerContent = content
            this.subsidyBannerColor = color
            this.subsidyShowBanner = shouldShowContent
        })

        const actionBannerDetails = this.actionBannerService.retrieveBannerDetails()
        this.actionBannerContent = actionBannerDetails.sidebarContent
        this.actionBannerColor = actionBannerDetails.color
    }

    getOptionKey(option) {
        return getDealOptionKey(option)
    }

    getOptionValue(option) {
        return getDealOptionValue(option)
    }

    checkBooleanDealOptionInclusion(option): boolean {
        // Towhitch, winter tires, and deductible are conventionally
        // returned by the API as BooleanDealOptions, but for
        // inventories they're returned as SelectableDealOptions.

        const value: boolean | undefined = (option as BooleanDealOption).value
        return typeof value !== 'undefined'
            ? value
            : !(option as SelectableDealOption).selectedValue.label.startsWith('No')
    }
}
